import React, { useState, useEffect, useRef } from 'react';
import { supabase } from '../../supabase-client.ts';
import UniversalReadingPage from './UniversalReadingPage.js';

// Cache for controlled settings to avoid repeated DB queries
const controlledCache = new Map();
const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes

const ConditionalReadingPage = ({ ...props }) => {
  const getCachedData = () => {
    const userEmail = localStorage.getItem('user_email');
    if (userEmail) {
      const cached = controlledCache.get(userEmail);
      if (cached && Date.now() - cached.timestamp < CACHE_DURATION) {
        return {
          userInfo: cached.userInfo
        };
      }
    }
    return {
      userInfo: {}
    };
  };

  const cachedData = getCachedData();
  const [loading, setLoading] = useState(false); // Start as false for immediate render
  const [userInfo, setUserInfo] = useState(cachedData.userInfo);
  const isInitialMount = useRef(true);

  useEffect(() => {
    const checkReadingAccess = async () => {
      try {
        const { data: { user } } = await supabase.auth.getUser();
        if (!user) {
          setLoading(false);
          return;
        }

        localStorage.setItem('user_email', user.email);

        const cached = controlledCache.get(user.email);
        if (cached && Date.now() - cached.timestamp < CACHE_DURATION) {
          setUserInfo(cached.userInfo);
          setLoading(false);
          return;
        }

        if (isInitialMount.current) {
          isInitialMount.current = false;
        } else {
          setLoading(true);
        }

        const { data: userData, error: userError } = await supabase
          .from('USERS')
          .select(`
            email, 
            cefr_level, 
            auth_id, 
            class_id,
            CLASSES(controlled)
          `)
          .eq('email', user.email)
          .single();

        if (userError || !userData) {
          const { data: fallbackData } = await supabase
            .from('USERS')
            .select('email, cefr_level, auth_id, class_id')
            .eq('email', user.email)
            .single();
          
          if (fallbackData) {
            setUserInfo(fallbackData);
            controlledCache.set(user.email, {
              userInfo: fallbackData,
              timestamp: Date.now()
            });
          }
          setLoading(false);
          return;
        }

        const userInfoData = {
          email: userData.email,
          cefr_level: userData.cefr_level,
          auth_id: userData.auth_id,
          class_id: userData.class_id
        };

        setUserInfo(userInfoData);

        controlledCache.set(user.email, {
          userInfo: userInfoData,
          timestamp: Date.now()
        });
      } catch (error) {
      } finally {
        setLoading(false);
      }
    };

    checkReadingAccess();
  }, []);

  return (
    <>
      {loading && (
        <div style={{ 
          position: 'fixed',
          top: '20px',
          right: '20px',
          padding: '6px 12px',
          backgroundColor: 'rgba(107, 63, 160, 0.85)',
          color: 'white',
          borderRadius: '16px',
          fontSize: '12px',
          zIndex: 1000,
          boxShadow: '0 2px 6px rgba(0,0,0,0.15)',
          opacity: 0.8
        }}>
          Updating...
        </div>
      )}
      
      <UniversalReadingPage userInfo={userInfo} {...props} />
    </>
  );
};

export default ConditionalReadingPage;
